import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { Tab, TabList, TabPanel, Tabs } from '../../../../../../CommonComponents/OPL/Tab';
import { TrustDTO, TrustsDTO } from '../../../../api/useTrustsQuery';
import { dataToForm, formToUpsertTrustBody, FormValues, validation } from './UpsertTrustModal.meta';
import { General } from './tabs/General/General';
import { useUpsertTrustMutation } from '../../../../api/useUpsertTrustMutation';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Beneficiaries } from './tabs/Beneficiaries/Beneficiaries';
import { PersonalInfoDTO } from '../../../../api/usePersonalInfoQuery';
import { Tooltip } from '../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { useSelector } from 'react-redux';
import { hasAccessToEditPersonalInfoSelector } from '../../../../../../CommonSelectors/CommonAreaSelector';

export const HEADER_ID = 'personal-info--upsert-trust--modal-header';

export interface Props {
  trust: Partial<TrustDTO>;
  data: TrustsDTO;
  onClose(): void;
  resetPersonalInfo(data: PersonalInfoDTO): void;
  isEstatePlanning?: boolean;
}

export const UpsertTrustModal: React.FC<Props> = ({
  trust,
  data,
  onClose,
  resetPersonalInfo,
  isEstatePlanning,
}) => {
  const hasAccessToEdit = useSelector(hasAccessToEditPersonalInfoSelector);

  const formBag = useForm({
    defaultValues: dataToForm(trust),
    disabled: !hasAccessToEdit,
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: upsertTrust,
    isLoading: isUpsertingTrust,
    error: upsertTrustError,
    reset: resetUpsertTrust,
  } = useUpsertTrustMutation({ resetPersonalInfo, isEstatePlanning });

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertTrust({
        body: formToUpsertTrustBody({ trustId: trust.Id, values }),
      });

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const hasValidationErrors = Object.keys(formBag.formState.errors).length > 0;

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: HEADER_ID }}
    >
      <ModalHeader as="h2" id={HEADER_ID}>
        Trust
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      {upsertTrustError && (
        <Alert
          message={upsertTrustError.message}
          onRequestClose={resetUpsertTrust}
          variant="error"
          mx="24"
          my="16"
        />
      )}

      <form onSubmit={formBag.handleSubmit(onSubmit)} id="trustsForm">
        <FormProvider {...formBag}>
          <Tabs>
            <TabList px="24">
              <Tab>General</Tab>
              <Tab>Beneficiaries</Tab>
            </TabList>

            <TabPanel>
              <General data={data} isEstatePlanning={isEstatePlanning} />
            </TabPanel>

            <TabPanel>
              <Beneficiaries data={data} />
            </TabPanel>
          </Tabs>

          <ModalFooter as="footer" hasBorder>
            <Button variant="subtleInactive" onClick={onClose}>
              Cancel
            </Button>

            <Tooltip
              content="Fill out required fields to proceed"
              visible={hasValidationErrors}
              placement="top-end"
            >
              <span>
                <Button
                  form="trustsForm"
                  type="submit"
                  isLoading={isUpsertingTrust}
                  ml="8"
                  disabled={hasValidationErrors || !hasAccessToEdit}
                >
                  Save Trust
                </Button>
              </span>
            </Tooltip>
          </ModalFooter>
        </FormProvider>
      </form>
    </Modal>
  );
};
